import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, ButtonGroup, Button } from "react-bootstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import {
  StudentAvatar, AttendanceButtons, useIsMobile,
  isInactiveStudent, isTransferredStudent,
} from "./guidanceShared";

const API = process.env.REACT_APP_API;

// แก้ไขการเช็คชื่อคาบแนะแนวย้อนหลัง — pattern เดียวกับ teaching/EditAttendance.js
export default function GuidanceEditAttendance() {
  const { guid_id, att_date } = useParams();
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState(null);
  const effectiveView = viewMode ?? (isMobile ? "card" : "table");

  const switchSkin = (skin) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (skin === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [data, setData] = useState([]);
  const [room, setRoom] = useState("");
  const [topic, setTopic] = useState("");

  // แปลง att_date (YYYY-MM-DD) เป็นรูปแบบ พ.ศ. สำหรับแสดงผล
  const displayDate = (() => {
    if (!att_date) return "";
    const [y, m, d] = att_date.split("-");
    return `${parseInt(d)}/${parseInt(m)}/${parseInt(y) + 543}`;
  })();

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${API}/getGuidanceAttendanceForEdit/${guid_id}/${att_date}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((response) => {
        const records = response.data;
        if (records.length > 0) {
          const first = records[0];
          setRoom(first.tb_class_c_id + "/" + first.r_no);
          setTopic(first.gtr_topic || "");
          const attendanceStudents = records.map((r) => ({
            study_id: r.study_id,
            st_id: r.st_id,
            st_idnumber: r.st_idnumber,
            st_name: r.st_name,
            st_status: r.st_status,
            st_photo: r.st_photo,
            attendanceStatus: r.att_status,
          }));

          return axiosInstance.get(`${API}/listStudentsInClass/${first.r_id}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          })
            .then((studentResponse) => {
              const studentMap = new Map(
                studentResponse.data.map((s) => [String(s.study_id), s])
              );
              const recordedStudyIds = new Set(
                attendanceStudents.map((student) => String(student.study_id))
              );

              // enrich recorded students with transfer info from student list
              const enrichedAttendance = attendanceStudents.map((s) => ({
                ...s,
                ...(studentMap.get(String(s.study_id))
                  ? {
                      transfer_room_id: studentMap.get(String(s.study_id)).transfer_room_id,
                      transfer_room_no: studentMap.get(String(s.study_id)).transfer_room_no,
                      transfer_class_id: studentMap.get(String(s.study_id)).transfer_class_id,
                    }
                  : {}),
              }));

              // add inactive or transferred students not in attendance records
              const unrecordedStudents = studentResponse.data
                .filter((student) =>
                  (isInactiveStudent(student) || isTransferredStudent(student)) &&
                  !recordedStudyIds.has(String(student.study_id))
                )
                .map((student) => ({ ...student, attendanceStatus: null }));

              setData([...enrichedAttendance, ...unrecordedStudents].sort((a, b) =>
                String(a.st_idnumber).localeCompare(String(b.st_idnumber))
              ));
            });
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: error.response?.data?.title || "เกิดข้อผิดพลาด",
          text: error.response?.data?.message || "ไม่สามารถโหลดข้อมูลได้",
        });
      })
      .finally(() => setLoading(false));
  }, [guid_id, att_date, setLoading]);

  const handleAttendanceChange = (index, status) => {
    const updated = [...data];
    updated[index].attendanceStatus = status;
    setData(updated);
  };

  const handleSaveAttendance = () => {
    setLoading(true);
    axiosInstance.put(`${API}/updateGuidanceAttendance`, {
      guid_id,
      att_date,
      topic,
      students: data
        .filter((student) => !isInactiveStudent(student) && !isTransferredStudent(student))
        .map((student) => ({
          study_id: student.study_id,
          attendanceStatus: student.attendanceStatus,
        })),
    }, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(() => {
        Swal.fire({
          title: "แก้ไขการเช็คชื่อสำเร็จ",
          icon: "success",
        }).then(() => {
          navigate("/advisor/guidance");
        });
      })
      .catch((err) => {
        Swal.fire({
          title: err.response?.data?.title || "เกิดข้อผิดพลาด",
          text: err.response?.data?.message || "ไม่สามารถบันทึกข้อมูลได้",
          icon: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/advisor/guidance">เช็คชื่อคาบแนะแนว</Link></li>
              <li className="breadcrumb-item active" aria-current="page">แก้ไข ชั้น ม.{room}</li>
            </ol>
            <h4 className="main-title mb-0">แก้ไขการเช็คชื่อคาบแนะแนว ม.{room}</h4>
          </div>
        </div>

        <Row className="g-12 mb-4">
          <div className="d-flex flex-wrap align-items-center gap-2 mt-3 mt-md-0">
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 ps-3" style={{ minWidth: "50px" }}>วันที่:</label>
              <span className="form-control" style={{ maxWidth: "180px", backgroundColor: "#f8f9fa" }}>{displayDate}</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 ps-3" style={{ minWidth: "50px" }}>กิจกรรม:</label>
              <input
                type="text"
                className="form-control"
                style={{ minWidth: "300px", maxWidth: "800px", width: "420px" }}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="ระบุกิจกรรมแนะแนว หรือหัวข้อที่พูดคุยกับนักเรียน"
              />
            </div>
          </div>
          <Col xl="12">
            <Card className="card-one">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-end mb-3">
                  <ButtonGroup size="sm">
                    <Button
                      variant={effectiveView === "card" ? "primary" : "outline-primary"}
                      onClick={() => setViewMode("card")}
                      title="มุมมองการ์ด"
                    >
                      <i className="ri-layout-grid-line"></i> การ์ด
                    </Button>
                    <Button
                      variant={effectiveView === "table" ? "primary" : "outline-primary"}
                      onClick={() => setViewMode("table")}
                      title="มุมมองตาราง"
                    >
                      <i className="ri-table-line"></i> ตาราง
                    </Button>
                  </ButtonGroup>
                </div>

                {effectiveView === "table" ? (
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <th scope="col" className="text-center" style={{ width: "50px" }}>เลขที่</th>
                        <th scope="col" className="text-center" style={{ width: "100px" }}>เลขประจำตัว</th>
                        <th scope="col">ชื่อ-สกุล</th>
                        <th scope="col" className="text-center" style={{ width: "150px" }}>สถานะการเข้าเรียน</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data && data.length > 0 ? (
                        data.map((student, index) => (
                          <tr key={student.study_id} className={isInactiveStudent(student) ? "table-danger" : isTransferredStudent(student) ? "table-warning" : ""}>
                            <th scope="row" className="text-center">{index + 1}</th>
                            <td className="text-center">{student.st_idnumber}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <StudentAvatar photo={student.st_photo} name={student.st_name} />
                                <span>
                                  {student.st_name}
                                  {isTransferredStudent(student) && (
                                    <small className="text-muted ms-2">(ย้ายไป ม.{student.transfer_class_id}/{student.transfer_room_no})</small>
                                  )}
                                </span>
                              </div>
                            </td>
                            <td className="text-center">
                              {!isInactiveStudent(student) && !isTransferredStudent(student) && (
                                <AttendanceButtons student={student} index={index} onChange={handleAttendanceChange} />
                              )}
                              {isInactiveStudent(student) && (
                                <span className="text-danger">จำหน่าย</span>
                              )}
                              {isTransferredStudent(student) && (
                                <span className="text-warning">ย้ายห้อง</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan={4} className="text-center">ไม่พบข้อมูล</td></tr>
                      )}
                    </tbody>
                  </Table>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {data && data.length > 0 ? (
                      data.map((student, index) => (
                        <Card
                          key={student.study_id}
                          className={`border ${isInactiveStudent(student) ? "border-danger bg-danger bg-opacity-10" : isTransferredStudent(student) ? "border-warning bg-warning bg-opacity-10" : ""}`}
                        >
                          <Card.Body className="p-0" style={{ minHeight: 76 }}>
                            <div className="d-flex" style={{ minHeight: 76 }}>
                              <div style={{ width: 64, flexShrink: 0, overflow: "hidden", borderRadius: "calc(0.375rem - 1px) 0 0 calc(0.375rem - 1px)" }}>
                                <StudentAvatar photo={student.st_photo} name={student.st_name} fill />
                              </div>
                              <div className="flex-grow-1 px-2 py-2 d-flex flex-column justify-content-between">
                                <div className="d-flex align-items-start gap-2">
                                  <span className="badge bg-secondary flex-shrink-0 mt-1">{index + 1}</span>
                                  <div className="flex-grow-1 text-end">
                                    <div className="fw-semibold" style={{ fontSize: "0.875rem" }}>{student.st_name}</div>
                                    <small className="text-muted">เลขประจำตัว {student.st_idnumber}</small>
                                    {isTransferredStudent(student) && (
                                      <div><small className="text-muted">(ย้ายไป ม.{student.transfer_class_id}/{student.transfer_room_no})</small></div>
                                    )}
                                  </div>
                                </div>
                                <div className="d-flex justify-content-end">
                                  {!isInactiveStudent(student) && !isTransferredStudent(student) && (
                                    <AttendanceButtons student={student} index={index} onChange={handleAttendanceChange} />
                                  )}
                                  {isInactiveStudent(student) && (
                                    <span className="text-danger">จำหน่าย</span>
                                  )}
                                  {isTransferredStudent(student) && (
                                    <span className="text-warning">ย้ายห้อง</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    ) : (
                      <div className="text-center text-muted py-3">ไม่พบข้อมูล</div>
                    )}
                  </div>
                )}

                <div className="d-flex justify-content-between mt-3">
                  <Link to="/advisor/guidance" className="btn btn-secondary btn-sm">
                    <i className="ri-arrow-left-line"></i> ย้อนกลับ
                  </Link>
                  <Button variant="primary" size="sm" onClick={handleSaveAttendance}>
                    <i className="ri-save-3-fill"></i> บันทึกการแก้ไข
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  );
}
